import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaSearch, FaFilter } from 'react-icons/fa';
import NotificationItem from '../components/NotificationItem';
import { useFollow } from '../utils/FollowContext';

export default function Notifications() {
  const auth = getAuth();
  const navigate = useNavigate();
  const { followers, following } = useFollow();
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTypeFilter, setActiveTypeFilter] = useState('All'); // Filter by notification type
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterPeopleYouFollow, setFilterPeopleYouFollow] = useState(false);
  const [filterPeopleFollowingYou, setFilterPeopleFollowingYou] = useState(false);
  const filterDropdownRef = useRef(null);

  // Fetch sender names for notifications and store them in the notification object
  useEffect(() => {
    if (!auth.currentUser) {
      navigate('/sign-in');
      return;
    }

    const q = query(
      collection(db, `notifications/${auth.currentUser.uid}/userNotifications`),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const notifs = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const notifData = {
            id: doc.id,
            ...doc.data()
          };
          // Fetch sender's name
          try {
            const senderRef = doc(db, 'users', notifData.senderId);
            const senderSnap = await getDoc(senderRef);
            if (senderSnap.exists()) {
              notifData.senderName = senderSnap.data().name || 'Unknown';
            } else {
              notifData.senderName = 'Unknown';
            }
          } catch (error) {
            console.error('Error fetching sender name for notification:', error);
            notifData.senderName = 'Unknown';
          }
          return notifData;
        })
      );
      setNotifications(notifs);
    }, (error) => {
      console.error('Error fetching notifications:', error);
    });

    return () => unsubscribe();
  }, [auth.currentUser, navigate]);

  // Handle click outside to close filter dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Apply all filters: search, type, and dropdown filters
  useEffect(() => {
    let filtered = [...notifications];

    // Filter by search query
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(notif =>
        notif.senderName.toLowerCase().includes(lowerQuery)
      );
    }

    // Filter by notification type
    if (activeTypeFilter !== 'All') {
      const typeMap = {
        Follows: 'follow',
        Likes: 'like',
        Comments: 'comment',
        Favorites: 'save'
      };
      const type = typeMap[activeTypeFilter];
      filtered = filtered.filter(notif => notif.type === type);
    }

    // Filter by "People you follow" and "People following you"
    if (filterPeopleYouFollow || filterPeopleFollowingYou) {
      filtered = filtered.filter(notif => {
        const senderId = notif.senderId;
        const isFollowedByYou = following?.includes(senderId) || false;
        const isFollowingYou = followers?.includes(senderId) || false;

        if (filterPeopleYouFollow && filterPeopleFollowingYou) {
          return isFollowedByYou && isFollowingYou;
        } else if (filterPeopleYouFollow) {
          return isFollowedByYou;
        } else if (filterPeopleFollowingYou) {
          return isFollowingYou;
        }
        return true;
      });
    }

    setFilteredNotifications(filtered);
  }, [notifications, searchQuery, activeTypeFilter, filterPeopleYouFollow, filterPeopleFollowingYou, followers, following]);

  // Handle Apply button in filter dropdown
  const handleApplyFilters = () => {
    setShowFilterDropdown(false);
  };

  // Handle Clear button in filter dropdown
  const handleClearFilters = () => {
    setFilterPeopleYouFollow(false);
    setFilterPeopleFollowingYou(false);
    setShowFilterDropdown(false);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black border-b border-gray-700 p-4">
        <h1 className="text-xl font-bold text-center">Activity</h1>
      </div>

      {/* Search Bar and Filters */}
      <div className="px-4 py-3">
        {/* Search Bar */}
        <div className="relative mb-4">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by user name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-2 bg-gray-900 border border-gray-600 rounded-full text-white placeholder-gray-400 focus:outline-none focus:border-gray-400"
          />
        </div>

        {/* Filter Tabs and Filter Icon */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          {['All', 'Follows', 'Likes', 'Comments', 'Favorites'].map(type => (
            <button
              key={type}
              onClick={() => setActiveTypeFilter(type)}
              className={`px-4 py-2 rounded-full font-medium text-sm transition ${
                activeTypeFilter === type
                  ? 'bg-blue-500/20 text-white'
                  : 'bg-gray-200 text-black hover:bg-gray-300'
              }`}
            >
              {type}
            </button>
          ))}
          <div className="relative">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="p-2 rounded-full bg-gray-200 text-black hover:bg-gray-300"
            >
              <FaFilter size={16} />
            </button>
            {showFilterDropdown && (
              <div
                ref={filterDropdownRef}
                className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg z-20"
              >
                <div className="p-4">
                  <h3 className="text-black font-semibold mb-4">Filter</h3>
                  <label className="flex items-center space-x-2 mb-3">
                    <input
                      type="checkbox"
                      checked={filterPeopleYouFollow}
                      onChange={(e) => setFilterPeopleYouFollow(e.target.checked)}
                      className="w-5 h-5 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                    />
                    <span className="text-black">People you follow</span>
                  </label>
                  <label className="flex items-center space-x-2 mb-4">
                    <input
                      type="checkbox"
                      checked={filterPeopleFollowingYou}
                      onChange={(e) => setFilterPeopleFollowingYou(e.target.checked)}
                      className="w-5 h-5 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                    />
                    <span className="text-black">People following you</span>
                  </label>
                  <div className="flex justify-between">
                    <button
                      onClick={() => setShowFilterDropdown(false)}
                      className="px-4 py-2 text-black font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleClearFilters}
                      className="px-4 py-2 text-pink-500 font-medium"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleApplyFilters}
                      className="px-4 py-2 bg-pink-500 text-white rounded-full font-medium"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="pb-4">
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notif) => (
            <NotificationItem key={notif.id} notification={notif} />
          ))
        ) : (
          <div className="text-center text-gray-400 py-8">
            No notifications found
          </div>
        )}
      </div>
    </div>
  );
}